import { useState } from 'react';
import type React from 'react';
import type { LoadingMigrateConfirmProps } from './loadingMigrateConfirm';

/**
 * Keeps a migration confirmation button in a busy state for as long as the confirm work runs. The loading flag is raised before awaiting the supplied confirm callback, any thrown error is logged rather than propagated, and the flag is lowered in a finally block.
 *
 * @param onConfirm - Async callback performing the migration confirmation.
 * @returns An object exposing isLoading and handleConfirm.
 */
const useLoadingMigrateConfirm = ({ onConfirm }: LoadingMigrateConfirmProps) => {
  const [isLoading, setIsLoading] = useState(false);

  /**
   * Runs the migration confirmation while holding the button in its loading state. The flag is raised, the supplied callback awaited, a thrown error logged to the console, and the flag lowered in a finally block so the button always recovers.
   *
   * @param e - Mouse event from the confirm button, forwarded to the callback.
   * @returns A promise resolving once the confirmation has settled and the loading state is cleared.
   */
  const handleConfirm = async (e: React.MouseEvent) => {
    setIsLoading(true);
    try {
      await onConfirm(e);
    } catch (error) {
      console.error('Error in handleConfirm:', error);
    } finally {
      setIsLoading(false);
    }
  };

  return { isLoading, handleConfirm };
};

export default useLoadingMigrateConfirm;
